// src/components/admin/EnvironmentManagement.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, Link } from 'react-router-dom';
import logger from '../../utils/logger';
import { 
  Server, PlusCircle, Database, 
  RefreshCw, Check, X, MoreHorizontal, 
  CheckCircle, XCircle, Edit, Trash2,
  Globe, ExternalLink, AlertTriangle
} from 'lucide-react';

/**
 * EnvironmentManagement Component - Manage Business Central environments
 * Includes listing, creation, and configuration of environments
 */
const EnvironmentManagement = ({ bcService }) => {
  const [environments, setEnvironments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  
  const navigate = useNavigate();
  
  // Fetch environments on component mount
  useEffect(() => {
    const fetchEnvironments = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch environments from BC service
        const response = await bcService.getAvailableEnvironments();
        
        // Format the data for our component
        if (Array.isArray(response)) {
          setEnvironments(response);
        } else if (response && Array.isArray(response.environments)) {
          setEnvironments(response.environments);
        } else {
          throw new Error('Invalid environment data format');
        }
      } catch (error) {
        logger.error('Error fetching environments:', error);
        setError('Failed to load environments. Please try again.');
        
        // Create mock data for development
        if (process.env.NODE_ENV === 'development') {
          const mockEnvironments = [
            {
              id: 'env-prod-01',
              name: 'Production',
              type: 'Production',
              url: 'https://businesscentral.dynamics.com/production',
              status: 'Active',
              isDefault: true,
              lastSync: '2025-05-01T10:15:00Z',
              version: '23.0.13234.0'
            },
            {
              id: 'env-sandbox-01',
              name: 'Sandbox',
              type: 'Sandbox',
              url: 'https://businesscentral.dynamics.com/sandbox',
              status: 'Active',
              isDefault: false,
              lastSync: '2025-04-25T14:30:00Z',
              version: '23.0.13234.0'
            }
          ];
          
          setEnvironments(mockEnvironments);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEnvironments();
  }, [bcService]);
  
  // Toggle action menu for an environment
  const toggleActionMenu = (envId) => {
    setShowActionMenu(showActionMenu === envId ? null : envId);
  };
  
  // Handle environment activation
  const handleActivateEnvironment = async (envId) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Find environment in the list
      const environment = environments.find(env => env.id === envId);
      if (!environment) {
        throw new Error('Environment not found');
      }
      
      // Call BC service to activate environment
      await bcService.setActiveEnvironment(environment.id, environment.name, true);
      
      // Update local state
      setEnvironments(environments.map(env => ({
        ...env,
        isDefault: env.id === envId
      })));
      
      // Close action menu
      setShowActionMenu(null);
    } catch (error) {
      logger.error('Error activating environment:', error);
      setError('Failed to activate environment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create new environment component
  const CreateEnvironmentModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      type: 'Production',
      url: '',
      companyId: '',
      companyName: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    
    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      
      if (!formData.name || !formData.url || !formData.companyName) {
        setFormError('Name, URL, and Company Name are required fields.');
        return;
      }
      
      try {
        setIsSubmitting(true);
        setFormError(null);
        
        // Call BC service to create environment
        const result = await bcService.createEnvironment(formData);
        
        // Add new environment to the list
        setEnvironments(prev => [...prev, result]);
        
        // Close modal
        setShowCreateModal(false);
      } catch (error) {
        logger.error('Error creating environment:', error);
        setFormError(error.message || 'Failed to create environment. Please try again.');
        
        // For development, add mock environment anyway
        if (process.env.NODE_ENV === 'development') {
          const newEnvironment = {
            id: `env-${Date.now()}`,
            name: formData.name,
            type: formData.type,
            url: formData.url,
            status: 'Active',
            isDefault: false,
            lastSync: new Date().toISOString(),
            version: '23.0.13234.0'
          };
          
          setEnvironments(prev => [...prev, newEnvironment]);
          setShowCreateModal(false);
        }
      } finally {
        setIsSubmitting(false);
      }
    };
    
    return (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Server className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Add New Environment</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Connect a new Business Central environment to your portal.
                    </p>
                  </div>
                </div>
              </div>
              
              {formError && (
                <div className="mt-4 p-2 bg-red-50 rounded-md">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
              
              <form className="mt-6" onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Environment Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="Production"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Environment Type
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="Production">Production</option>
                      <option value="Sandbox">Sandbox</option>
                      <option value="Test">Test</option>
                      <option value="Development">Development</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                      Environment URL
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="url"
                        name="url"
                        value={formData.url}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="https://businesscentral.dynamics.com/production"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="companyId" className="block text-sm font-medium text-gray-700">
                      Company ID (Optional)
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="companyId"
                        name="companyId"
                        value={formData.companyId}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                      Company Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        placeholder="CRONUS UK Ltd."
                      />
                    </div>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Creating...
                  </>
                ) : 'Add Environment'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  if (isLoading && environments.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Environments
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your Business Central environments
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusCircle className="-ml-1 mr-2 h-5 w-5" />
            Add Environment
          </button>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Environments list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {environments.length === 0 ? (
            <li className="px-6 py-10 text-center">
              <Server className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No environments found.</p>
              <p className="mt-1 text-sm text-gray-500">Get started by adding your first environment.</p>
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="mt-6 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PlusCircle className="-ml-1 mr-2 h-5 w-5" />
                Add Environment
              </button>
            </li>
          ) : (
            environments.map((env) => (
              <li key={env.id}>
                <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                  <div className="flex-1 flex items-center min-w-0">
                    {/* Environment icon */}
                    <div className="flex-shrink-0">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                        env.type === 'Production' 
                          ? 'bg-green-100' 
                          : env.type === 'Sandbox' 
                            ? 'bg-blue-100' 
                            : 'bg-purple-100'
                      }`}>
                        <Server className={`h-6 w-6 ${
                          env.type === 'Production' 
                            ? 'text-green-600' 
                            : env.type === 'Sandbox' 
                              ? 'text-blue-600' 
                              : 'text-purple-600'
                        }`} />
                      </div>
                    </div>
                    
                    {/* Environment details */}
                    <div className="ml-4 flex-1">
                      <div className="flex items-center">
                        <h3 className="text-base font-medium text-gray-900 mr-2">{env.name}</h3>
                        {env.isDefault && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            Default
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <Globe className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                          <span className="truncate">{env.url}</span>
                        </div>
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <Database className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
                          {env.version || 'Unknown version'}
                        </div>
                      </div>
                      <div className="mt-1 text-sm text-gray-500">
                        Last synchronized: {formatDate(env.lastSync)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="ml-5 flex-shrink-0 flex items-center">
                    {/* External link button */}
                    <a
                      href={env.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 mr-2 text-indigo-600 hover:text-indigo-900"
                    >
                      <ExternalLink className="h-5 w-5" />
                    </a>
                    
                    {/* Actions dropdown */}
                    <div className="relative inline-block text-left">
                      <div>
                        <button
                          type="button"
                          className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                          id={`options-menu-${env.id}`}
                          aria-expanded="true"
                          aria-haspopup="true"
                          onClick={() => toggleActionMenu(env.id)}
                        >
                          <span className="sr-only">Open options</span>
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                      </div>
                      
                      {showActionMenu === env.id && (
                        <div
                          className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10"
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby={`options-menu-${env.id}`}
                        >
                          <div className="py-1" role="none">
                            {!env.isDefault && (
                              <button
                                onClick={() => handleActivateEnvironment(env.id)}
                                className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                role="menuitem"
                              >
                                <CheckCircle className="mr-3 h-5 w-5 text-green-500" />
                                Set as Default
                              </button>
                            )}
                            <button
                              onClick={() => {
                                // Handle sync environment
                                logger.log(`Sync environment ${env.id}`);
                                setShowActionMenu(null);
                              }}
                              className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              role="menuitem"
                            >
                              <RefreshCw className="mr-3 h-5 w-5 text-blue-500" />
                              Sync Environment
                            </button>
                            <button
                              onClick={() => navigate(`/admin/environments/edit/${env.id}`)}
                              className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              role="menuitem"
                            >
                              <Edit className="mr-3 h-5 w-5 text-gray-500" />
                              Edit Environment
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm("Are you sure you want to delete this environment?")) {
                                  // Handle environment deletion
                                  logger.log(`Delete environment ${env.id}`);
                                }
                              }}
                              className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                              role="menuitem"
                            >
                              <Trash2 className="mr-3 h-5 w-5 text-red-500" />
                              Delete Environment
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
      
      {/* Create Environment Modal */}
      {showCreateModal && <CreateEnvironmentModal />}
    </div>
  );
};

export default EnvironmentManagement;